import * as React from "react";
import { CircleAlert } from "lucide-react";

import { headingTag, useHeadingLevel, type HeadingLevel } from "../lib/heading";
import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 EmptyState (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * Icon, title, description, actions; all strings are content props (break #73).
 *
 * Sizes:
 * - `inline` (inside a card or a table row): bare 20px icon, 14px title,
 *   Inter 14 description, actions 16 below.
 * - `page` (a whole screen: not found, not enabled, offboarded): the icon in a
 *   40px disc, 18px title (h2 by default), actions 24 below.
 *
 * Variants:
 * - `empty` (default): neutral, `--ink-muted` icon on `--muted`. Also the
 *   look for "not found" and "not enabled": nothing failed there.
 * - `error`: something actually failed ("Daten konnten nicht geladen
 *   werden"). The icon turns `--destructive-accent` (on `--destructive-subtle`
 *   at page size), default `CircleAlert`, and icon + title + description are a
 *   `role="alert"`, so a failure that replaces a loading state is announced.
 *   The actions sit outside the alert and are not read with it.
 */

export type EmptyStateSize = "inline" | "page";
export type EmptyStateVariant = "empty" | "error";

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: IconComponent;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons, usually one: inline `size="sm"`, page `md`. */
  actions?: React.ReactNode;
  size?: EmptyStateSize;
  variant?: EmptyStateVariant;
  /** Heading level of the title. Default: a `p` inline; on a page the context level (h2 in PageLayout). */
  headingLevel?: HeadingLevel;
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, actions, size = "inline", variant = "empty", headingLevel, className, ...props },
  ref,
) {
  const page = size === "page";
  const error = variant === "error";
  const glyph = icon ?? (error ? CircleAlert : undefined);
  const contextLevel = useHeadingLevel(undefined, 2);
  // Inline (inside a card, table or pane) the title is a line of text unless a level is asked for.
  const Title = headingLevel ? headingTag(headingLevel) : page ? headingTag(contextLevel) : "p";
  const tone = error ? "text-destructive-accent" : "text-ink-muted";

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-center text-center",
        // A page state caps at the reading width, left-aligned with the header (finding #11).
        page ? "w-full max-w-3xl self-start px-6 py-12" : "px-4 py-8",
        className,
      )}
      {...props}
    >
      <div role={error ? "alert" : undefined} className="flex flex-col items-center">
        {glyph &&
          (page ? (
            <span
              data-slot="icon"
              className={cn(
                "mb-4 flex size-10 items-center justify-center rounded-full",
                error ? "bg-destructive-subtle" : "bg-muted",
                tone,
              )}
            >
              <Icon icon={glyph} size={20} />
            </span>
          ) : (
            <span data-slot="icon" className={cn("mb-3 flex", tone)}>
              <Icon icon={glyph} size={20} />
            </span>
          ))}
        <Title className={cn("font-sans text-ink", page ? "text-heading-md" : "text-label")}>{title}</Title>
        {description != null && (
          <p className={cn("mt-1 font-body text-body text-ink-muted", page ? "max-w-md" : "max-w-sm")}>{description}</p>
        )}
      </div>
      {actions != null && (
        <div className={cn("flex flex-wrap items-center justify-center gap-2", page ? "mt-6" : "mt-4")}>{actions}</div>
      )}
    </div>
  );
});

export { EmptyState };
